import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Upload, RotateCcw, Download, Loader2, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PRESET_MODELS = [
  { id: "m1", label: "Model 1", url: "https://static.prod-images.emergentagent.com/jobs/60c5494e-1909-4a3f-9e2e-6ab4bb3ab84e/images/5f9e62ecc518a76365c3cd7512e67b37b4be28b561033903c94e2ca49dadf7f2.jpeg" },
  { id: "m2", label: "Model 2", url: "https://static.prod-images.emergentagent.com/jobs/60c5494e-1909-4a3f-9e2e-6ab4bb3ab84e/images/17d379ed38707b52ceeb0c245324e83948a1fca86cebdee4a098e66bc6e4f94f.jpeg" },
  { id: "m3", label: "Model 3", url: "https://static.prod-images.emergentagent.com/jobs/60c5494e-1909-4a3f-9e2e-6ab4bb3ab84e/images/dd001b72f49bff32ad7a45236a515932b66c6046426cd7195979e4d2b0e06b78.jpeg" },
];

const resizeToDataUrl = (file, max = 1024) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.9));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const TryOnModal = ({ open, onOpenChange, product, onAddToCart }) => {
  const fileRef = useRef(null);
  const [preset, setPreset] = useState(null);
  const [upload, setUpload] = useState(null); // data url
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setPreset(null); setUpload(null); setResult(null); setLoading(false); };

  const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    try {
      const dataUrl = await resizeToDataUrl(file);
      setUpload(dataUrl);
      setPreset(null);
    } catch {
      toast.error("Couldn't read that image. Try another photo.");
    }
  };

  const generate = async () => {
    if (!upload && !preset) { toast.error("Upload a photo or pick a model first."); return; }
    setLoading(true);
    setResult(null);
    try {
      const body = {
        garment_image_url: product.images?.[0],
        product_title: product.title,
        ...(upload ? { person_image_b64: upload } : { person_image_url: preset }),
      };
      const { data } = await api.post("/tryon", body, { timeout: 120000 });
      setResult(data.image);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Try-on failed. Please try a clear, full-length photo.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = result;
    a.download = `${product.slug}-tryon.png`;
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="tryon-modal"
        className="bg-[#FFFFFF] max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto p-0 border border-[#E5E5DF]"
      >
        <div className="px-6 sm:px-8 py-5 border-b border-[#E5E5DF]">
          <DialogTitle className="font-display text-2xl sm:text-3xl tracking-tight flex items-center gap-2">
            <Sparkles size={20} className="text-[#B84A3B]" /> Virtual Try-On
          </DialogTitle>
          <p className="text-[0.72rem] font-mono text-[#6E6E69] mt-1 tracking-wide">
            AI preview of “{product.title}” — a styling guide, not an exact fit.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 sm:p-8">
          {/* Left: choose source / result */}
          <div>
            {!result && !loading && (
              <div className="space-y-6">
                <div>
                  <span className="overline text-[#1A1A1A]">Choose a model</span>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {PRESET_MODELS.map((m) => (
                      <button key={m.id} data-testid={`tryon-preset-${m.id}`}
                        onClick={() => { setPreset(m.url); setUpload(null); }}
                        className={`relative aspect-[3/4] overflow-hidden border-2 transition-colors ${preset === m.url ? "border-[#B84A3B]" : "border-transparent hover:border-[#E5E5DF]"}`}>
                        <img src={m.url} alt={m.label} className="w-full h-full object-cover bg-[#EFEFEA]" />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className="h-px flex-1 bg-[#E5E5DF]" />
                  <span className="text-[0.65rem] font-mono text-[#9a9a94] tracking-widest">OR</span>
                  <span className="h-px flex-1 bg-[#E5E5DF]" />
                </div>

                <div>
                  <span className="overline text-[#1A1A1A]">Upload your photo</span>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="tryon-file-input" />
                  <button data-testid="tryon-upload-btn" onClick={() => fileRef.current?.click()}
                    className="mt-3 w-full flex items-center justify-center gap-2 border border-dashed border-[#B0B0A8] py-6 text-[0.7rem] font-mono tracking-[0.16em] uppercase text-[#6E6E69] hover:border-[#1A1A1A] hover:text-[#1A1A1A] transition-colors">
                    {upload ? (
                      <img src={upload} alt="Your upload" className="h-24 w-auto object-contain" />
                    ) : (
                      <><Upload size={16} /> Upload a full-length photo</>
                    )}
                  </button>
                  <p className="text-[0.62rem] text-[#9a9a94] mt-2 leading-relaxed">
                    Best results: a clear, front-facing, full-body photo. Photos are used only to create your preview.
                  </p>
                </div>

                <button data-testid="tryon-generate-btn" onClick={generate} disabled={!upload && !preset}
                  className="w-full bg-[#1A1A1A] text-[#FFFFFF] py-4 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-[#B84A3B] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  <Sparkles size={15} /> Generate Try-On
                </button>
              </div>
            )}

            {loading && (
              <div className="flex flex-col items-center justify-center text-center py-16" data-testid="tryon-loading">
                <Loader2 size={34} className="text-[#B84A3B] animate-spin" />
                <p className="font-display text-xl mt-5">Styling your look…</p>
                <p className="text-[0.7rem] font-mono text-[#6E6E69] mt-1">This usually takes 10–20 seconds</p>
              </div>
            )}

            {result && !loading && (
              <div className="space-y-3">
                <button data-testid="tryon-again-btn" onClick={reset}
                  className="w-full flex items-center justify-center gap-2 border border-[#1A1A1A] py-3 text-[0.7rem] font-mono tracking-[0.18em] uppercase hover:bg-[#1A1A1A] hover:text-[#FFFFFF] transition-colors">
                  <RotateCcw size={14} /> Try Another
                </button>
                <button data-testid="tryon-download-btn" onClick={download}
                  className="w-full flex items-center justify-center gap-2 border border-[#E5E5DF] py-3 text-[0.7rem] font-mono tracking-[0.18em] uppercase hover:border-[#1A1A1A] transition-colors">
                  <Download size={14} /> Download
                </button>
                {onAddToCart && (
                  <button data-testid="tryon-add-cart-btn" onClick={() => { onAddToCart(); handleClose(false); }}
                    className="w-full flex items-center justify-center gap-2 bg-[#B84A3B] text-white py-3.5 text-[0.7rem] font-mono tracking-[0.18em] uppercase hover:bg-[#1A1A1A] transition-colors">
                    <ShoppingBag size={14} /> Add to Cart
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Right: preview canvas */}
          <div className="order-first md:order-last">
            <div className="aspect-[3/4] bg-[#EFEFEA] overflow-hidden flex items-center justify-center" data-testid="tryon-preview">
              {result ? (
                <img src={result} alt="Your virtual try-on" className="w-full h-full object-cover" data-testid="tryon-result-image" />
              ) : loading ? (
                <div className="w-full h-full animate-pulse bg-[#E5E5DF]" />
              ) : (
                <img src={product.images?.[0]} alt={product.title} className="w-full h-full object-cover opacity-90" />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
